import { useState } from 'react';
import { Box, Button, Dialog, Radio, Typography } from '@mui/material';

import { DIALOG_BG } from '../share/dialogParts';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * "Are you sure you want to clear your schedule?", with the scope choice.
 *
 * Lifted out of `Schedule.tsx` on Sept 19 so the account page can ask the same
 * question rather than grow a second one beside it: two dialogs about the same
 * irreversible act would drift, and the day they disagreed one of them would be
 * wrong about what it was going to delete.
 */

export type ClearScope = 'all' | 'scheduled';

interface Props {
  open: boolean;
  onClose: () => void;
  onClear: (scope: ClearScope) => void;
  /** How many events each scope would take, so the button can say so. */
  counts: { all: number; scheduled: number };
}

export default function ClearScheduleDialog({ open, onClose, onClear, counts }: Props) {
  const [scope, setScope] = useState<ClearScope>('all');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="clear-schedule-title"
      slotProps={{
        paper: { sx: { background: DIALOG_BG, backgroundImage: 'none', borderRadius: 3, p: 3, minWidth: 360 } },
      }}
    >
      <Typography
        id="clear-schedule-title"
        sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 19, mb: 0.5, color: BRAND.parchment }}
      >
        Are you sure you want to clear your schedule?
      </Typography>
      <Typography sx={{ fontSize: 12.5, opacity: 0.65, mb: 2, color: BRAND.parchment }}>
        You can undo this straight away with ⌘Z.
      </Typography>

      {(
        [
          ['all', `Everything (${counts.all} events)`, 'Scheduled and saved for later'],
          ['scheduled', `Scheduled only (${counts.scheduled} events)`, 'Keeps your saved-for-later list'],
        ] as const
      ).map(([value, label, hint]) => (
        <Box
          key={value}
          onClick={() => setScope(value)}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: 1,
            borderRadius: 2,
            cursor: 'pointer',
            color: BRAND.parchment,
            '&:hover': { background: 'rgba(243,238,231,0.06)' },
          }}
        >
          <Radio
            checked={scope === value}
            size="small"
            slotProps={{ input: { 'aria-label': label } }}
            sx={{ color: 'rgba(243,238,231,0.6)', p: 0.4, '&.Mui-checked': { color: BRAND.parchment } }}
          />
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{label}</Typography>
            <Typography sx={{ fontSize: 11.5, opacity: 0.6 }}>{hint}</Typography>
          </Box>
        </Box>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(243,238,231,0.7)', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            onClear(scope);
            onClose();
          }}
          sx={{
            background: '#ef3d23',
            color: BRAND.parchment,
            fontWeight: 700,
            borderRadius: 999,
            px: 3,
            '&:hover': { background: '#d43318' },
          }}
        >
          {scope === 'all' ? 'Clear everything' : 'Clear schedule'}
        </Button>
      </Box>
    </Dialog>
  );
}
